// src/components/PursuitIntelligenceDoc.jsx
// Pursuit Intelligence Document (PID) — structured, CRUD-capable pursuit artifact
// Replaces the flat text renderer in the Business Case tab of OpportunitiesTab
//
// Data sources:
//   - MEDDIC: read from existing Notion fields via detail.js (meddicMetrics, etc.)
//   - Strategic Positioning, Entry Points, Open Questions, Session Log:
//     read/written via api/opportunities/business-case.js (JSON in Internal Briefing)

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './PursuitIntelligenceDoc.css';
import { useProfile } from '../context/UserProfileContext';

// ── MEDDIC field config ────────────────────────────────────────────────────
const MEDDIC_FIELDS = [
  { key: 'metrics',          label: 'Metrics',           letter: 'M', hint: 'Quantifiable value — revenue impact, cost savings, risk reduction. What does success look like in numbers?', ai: 'Victor' },
  { key: 'economicBuyer',    label: 'Economic Buyer',    letter: 'E', hint: 'Who controls the budget and has final authority to sign? Not the champion — the person who writes the check.', ai: 'Victor' },
  { key: 'decisionCriteria', label: 'Decision Criteria', letter: 'D', hint: 'What criteria will they use to evaluate vendors? Technical fit, price, references, implementation risk?', ai: 'Victor' },
  { key: 'decisionProcess',  label: 'Decision Process',  letter: 'D', hint: 'How will they make this decision? Who is involved, what are the steps, what is the timeline?', ai: 'Victor' },
  { key: 'identifiedPain',   label: 'Identified Pain',   letter: 'I', hint: 'What is the specific business pain driving this? Operational friction, risk, cost, competitive pressure?', ai: 'Selma' },
  { key: 'champion',         label: 'Champion',          letter: 'C', hint: 'Who is internally selling this on your behalf? Not just a supporter — someone with skin in the game.', ai: 'Victor' },
];

// ── MEDDIC next best action playbook ──────────────────────────────────────
const MEDDIC_PLAYBOOK = {
  metrics: [
    'Quantify the cost of the current problem — what does failure cost per month?',
    'Estimate the value of success: revenue protected, cost avoided, time saved.',
    'Ask: "How will your leadership measure whether this program succeeded?"',
  ],
  economicBuyer: [
    'Ask your champion: "Who ultimately signs off on an investment at this level?"',
    'Confirm whether it is the CIO, CFO, COO, or a committee decision.',
    'Ask: "What does the approval process look like above your level?"',
  ],
  decisionCriteria: [
    'Ask: "What does a successful vendor look like to your team?"',
    'Probe for hidden criteria: references, depth in their market, implementation speed.',
    'Ask: "What would disqualify a vendor at this stage?"',
  ],
  decisionProcess: [
    'Map the steps: discovery → shortlist → proposal → approval → contract.',
    'Ask: "Who else needs to be involved before a decision gets made?"',
    'Identify if procurement, legal, or a committee has a veto.',
  ],
  identifiedPain: [
    'Get specific: what broke, who felt it, how often, at what cost?',
    'Ask: "What happens if this problem is not solved in the next 6 months?"',
    'Connect pain to career risk for your champion and economic buyer.',
  ],
  champion: [
    'Test willingness: "Would you be comfortable introducing us to your CIO?"',
    'Ask: "Is there anyone internally who could help us understand the decision process better?"',
    'Watch for: does he volunteer information, share internal docs, create meetings unprompted?',
  ],
};

const DISPLACEMENT_OPTIONS = ['', 'Complement', 'Greenfield', 'Displace'];
const ENTRY_POINT_STATUSES  = ['Proposed', 'Active', 'Won', 'Lost'];

// ── Status badge logic ─────────────────────────────────────────────────────
function meddicStatus(value) {
  if (!value || value.trim().length === 0) return 'missing';
  if (value.trim().length < 40)            return 'partial';
  return 'complete';
}

function StatusBadge({ status }) {
  const cfg = {
    complete: { label: 'Complete', color: '#16a34a', bg: 'var(--dm-green-wash)' },
    partial:  { label: 'Partial',  color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)' },
    missing:  { label: 'Missing',  color: 'var(--dm-red)', bg: 'var(--dm-red-light)' },
  }[status];
  return (
    <span className="pid-status-badge" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

// ── Inline editable field ─────────────────────────────────────────────────
function EditableField({ value, onSave, placeholder, multiline = true, disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const handleSave = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };

  if (disabled) {
    return <div className="pid-field-value pid-field-readonly">{value || <span className="pid-field-empty">{placeholder}</span>}</div>;
  }

  if (!editing) {
    return (
      <div className="pid-field-value pid-field-clickable" onClick={() => setEditing(true)} title="Click to edit">
        {value ? value : <span className="pid-field-empty">{placeholder}</span>}
        <span className="pid-edit-icon">✎</span>
      </div>
    );
  }

  return multiline ? (
    <textarea
      className="pid-field-editor"
      value={draft}
      autoFocus
      onChange={e => setDraft(e.target.value)}
      onBlur={handleSave}
      onKeyDown={e => { if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
      placeholder={placeholder}
      rows={4}
    />
  ) : (
    <input
      className="pid-field-editor pid-field-editor--single"
      type="text"
      value={draft}
      autoFocus
      onChange={e => setDraft(e.target.value)}
      onBlur={handleSave}
      onKeyDown={e => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
      }}
      placeholder={placeholder}
    />
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────
function Section({ title, badge, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pid-section">
      <button className="pid-section-header" onClick={() => setOpen(o => !o)}>
        <span className="pid-section-title">{title}</span>
        {badge && <span className="pid-section-badge">{badge}</span>}
        <span className="pid-section-chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="pid-section-body">{children}</div>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function PursuitIntelligenceDoc({ opportunityId, opportunityName, initialMeddic }) {
  const { firstName, companyName } = useProfile();
  const [pid,     setPid]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [toast,   setToast]   = useState('');

  // Load PID on mount
  useEffect(() => {
    if (!opportunityId) return;
    setLoading(true);
    apiFetch(`/api/opportunities/business-case?id=${opportunityId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          // Merge MEDDIC from initialMeddic (detail.js) — richer source
          setPid({
            ...data.pid,
            meddic: {
              ...data.pid.meddic,
              ...(initialMeddic || {}),
            },
          });
        } else {
          setError('Could not load pursuit document');
        }
      })
      .catch(() => setError('Network error loading pursuit document'))
      .finally(() => setLoading(false));
  }, [opportunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // Auto-seed open questions on first load when list is empty and MEDDIC has gaps
  useEffect(() => {
    if (!pid) return;
    if ((pid.openQuestions || []).length > 0) return;
    const meddic = pid.meddic || {};
    const missing = MEDDIC_FIELDS.filter(f => meddicStatus(meddic[f.key]) === 'missing');
    if (missing.length === 0) return;

    // Seed with the top coaching question for each missing MEDDIC field
    const seedQuestions = missing.slice(0, 5).map(f => ({
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      text: MEDDIC_PLAYBOOK[f.key]?.[0] || ('Identify: ' + f.label),
      ai: 'Victor',
      timestamp: new Date().toISOString(),
      resolved: false,
    }));

    // Optimistically update local state first
    setPid(prev => ({ ...prev, openQuestions: seedQuestions }));

    // Persist each seeded question individually via add_question so they survive navigation
    // Fire sequentially (not parallel) to avoid race conditions on the Notion write
    (async () => {
      for (const q of seedQuestions) {
        await apiFetch('/api/opportunities/business-case', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: opportunityId,
            updates: { add_question: { text: q.text, ai: q.ai } },
            updatedBy: 'Victor',
          }),
        }).catch(() => {});
      }
    })();
  }, [opportunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generic save — posts update to API and merges response back
  const save = useCallback(async (updates, meddic = null) => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/opportunities/business-case', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opportunityId, updates, meddic, updatedBy: firstName || 'user' }),
      });
      const data = await res.json();
      if (data.success) {
        setPid(prev => ({ ...prev, ...data.pid, meddic: { ...prev.meddic, ...data.pid.meddic } }));
        showToast('Saved ✓');
      } else {
        showToast('Save failed — try again');
      }
    } catch {
      showToast('Save failed — network error');
    } finally {
      setSaving(false);
    }
  }, [opportunityId, firstName]);

  // Strategic field save
  const saveStrategic = (field, value) => save({ strategic: { [field]: value } });

  // Business case save — Phase A.5a
  // Persists to pid.businessCase. War Room sessions write here automatically;
  // this handler also lets the user edit manually from the PID.
  const saveBusinessCase = (fields) => {
    setPid(prev => ({
      ...prev,
      businessCase: { ...(prev.businessCase || {}), ...fields },
    }));
    save({ business_case: { ...(pid?.businessCase || {}), ...fields } });
  };

  // MEDDIC save — goes to proper Notion fields
  const saveMeddic = (field, value) => {
    setPid(prev => ({ ...prev, meddic: { ...prev.meddic, [field]: value } }));
    save({}, { [field]: value });
  };

  // Entry point operations
  const addEntryPoint = () => save({
    add_entry_point: { name: 'New Entry Point', priceLow: '', priceHigh: '', timeline: '', status: 'Proposed', notes: '', trigger: '' }
  });
  const updateEntryPoint = (id, fields) => {
    // Optimistic update
    setPid(prev => ({
      ...prev,
      entryPoints: (prev.entryPoints || []).map(ep => ep.id === id ? { ...ep, ...fields } : ep),
    }));
    save({ update_entry_point: { id, ...fields } });
  };
  const removeEntryPoint = (id) => {
    setPid(prev => ({ ...prev, entryPoints: (prev.entryPoints || []).filter(ep => ep.id !== id) }));
    save({ remove_entry_point: id });
  };

  // Open questions
  const [newQuestion, setNewQuestion] = useState('');
  const addQuestion = () => {
    if (!newQuestion.trim()) return;
    save({ add_question: { text: newQuestion.trim(), ai: firstName || 'user' } });
    setNewQuestion('');
  };
  const resolveQuestion = (id) => save({ resolve_question: id });

  // Compute overall MEDDIC score
  const meddicComplete = pid ? MEDDIC_FIELDS.filter(f => meddicStatus(pid.meddic?.[f.key]) === 'complete').length : 0;

  if (loading) return <div className="pid-loading"><div className="pid-spinner" />Loading pursuit document…</div>;
  if (error)   return <div className="pid-error">{error}</div>;
  if (!pid)    return null;

  const strategic    = pid.strategic    || {};
  const businessCase = pid.businessCase || null;
  const entryPoints  = pid.entryPoints  || [];
  const openQs       = pid.openQuestions|| [];
  const sessionLog   = pid.sessionLog   || [];
  const meddic       = pid.meddic       || {};

  // ── Entry points total calculation ───────────────────────────────────────
  const parsePrice = (str) => {
    if (!str) return null;
    const clean = str.replace(/[$,\s]/g, '').toUpperCase();
    const m = clean.match(/^([\d.]+)([KM]?)$/);
    if (!m) return null;
    const n = parseFloat(m[1]);
    if (isNaN(n)) return null;
    return m[2] === 'M' ? n * 1000000 : m[2] === 'K' ? n * 1000 : n;
  };
  const fmtPrice = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`;
  const epTotalLow  = entryPoints.map(ep => parsePrice(ep.priceLow)).filter(n => n != null).reduce((a, b) => a + b, 0);
  const epTotalHigh = entryPoints.map(ep => parsePrice(ep.priceHigh)).filter(n => n != null).reduce((a, b) => a + b, 0);
  const epHasPrices = epTotalLow > 0 || epTotalHigh > 0;

  return (
    <div className="pid-root">
      {toast && <div className="pid-toast">{toast}</div>}
      {saving && <div className="pid-saving-indicator">Saving…</div>}

      <div className="pid-header">
        <div className="pid-header-title">Pursuit Intelligence Document</div>
        <div className="pid-header-opp">{opportunityName}</div>
        {pid.lastUpdated && (
          <div className="pid-header-meta">
            Last updated {new Date(pid.lastUpdated).toLocaleDateString()} by {pid.lastUpdatedBy}
          </div>
        )}
      </div>

      {/* ── SECTION 1: STRATEGIC POSITIONING ─────────────────────────── */}
      <Section title="Strategic Positioning" defaultOpen={true}>
        <div className="pid-field-group">
          <div className="pid-field">
            <div className="pid-field-label">
              Opportunity Angle
              <span className="pid-field-ai">Victor</span>
            </div>
            <EditableField
              value={strategic.opportunityAngle}
              onSave={v => saveStrategic('opportunityAngle', v)}
              placeholder={`What is the specific angle ${companyName} is pursuing? e.g. 'Complementary carve-out — independent oversight alongside the incumbent prime'`}
            />
          </div>
          <div className="pid-field">
            <div className="pid-field-label">
              Win Theme
              <span className="pid-field-ai">Victor</span>
            </div>
            <EditableField
              value={strategic.winTheme}
              onSave={v => saveStrategic('winTheme', v)}
              placeholder={`The one thing that makes ${companyName} the right choice. e.g. 'Depth in their market that a generalist cannot credibly offer'`}
            />
          </div>
          <div className="pid-field">
            <div className="pid-field-label">
              Competitive Position
              <span className="pid-field-ai">Victor</span>
            </div>
            <EditableField
              value={strategic.competitivePosition}
              onSave={v => saveStrategic('competitivePosition', v)}
              placeholder="Who are we competing against and how do we differentiate?"
            />
          </div>
          <div className="pid-field">
            <div className="pid-field-label">Pursuit Mode</div>
            <select
              className="pid-select"
              value={strategic.displacementStrategy || ''}
              onChange={e => saveStrategic('displacementStrategy', e.target.value)}
            >
              {DISPLACEMENT_OPTIONS.map(o => <option key={o} value={o}>{o || '— Select —'}</option>)}
            </select>
          </div>
        </div>
      </Section>

      {/* ── SECTION 1.5: BUSINESS CASE ──────────────────────────────── */}
      {/* Phase A.5a: dedicated business case slot. Written by War Room sessions
          via api/warroom/learn.js → api/opportunities/business-case.js, or
          edited manually here. Business cases are opportunity-level artifacts;
          the Floor does not write them. */}
      <Section
        title="Business Case"
        badge={businessCase?.content ? 'On file' : 'Not yet built'}
        defaultOpen={!!businessCase?.content}
      >
        {!businessCase?.content && (
          <div className="pid-empty-state">
            No business case built yet. Victor will build one in a War Room session, or you can edit one in below.
          </div>
        )}
        <div className="pid-field-group">
          <div className="pid-field">
            <div className="pid-field-label">
              Title
              <span className="pid-field-ai">Victor</span>
            </div>
            <EditableField
              value={businessCase?.title || ''}
              onSave={v => saveBusinessCase({ title: v })}
              placeholder="e.g. 'Readiness Assessment — de-risking the incumbent prime'"
              multiline={false}
            />
          </div>
          <div className="pid-field">
            <div className="pid-field-label">
              Executive Summary
              <span className="pid-field-ai">Victor</span>
            </div>
            <EditableField
              value={businessCase?.summary || ''}
              onSave={v => saveBusinessCase({ summary: v })}
              placeholder="2-3 sentence summary an executive could read in 30 seconds and grasp the deal."
            />
          </div>
          <div className="pid-field">
            <div className="pid-field-label">
              Full Content
              <span className="pid-field-ai">Victor</span>
            </div>
            <EditableField
              value={businessCase?.content || ''}
              onSave={v => saveBusinessCase({ content: v })}
              placeholder="Full business case — value drivers, ROI analysis, risk mitigation, recommended approach. Built and saved automatically from War Room sessions."
            />
          </div>
          {businessCase?.builtAt && (
            <div className="pid-header-meta">
              Last built {new Date(businessCase.builtAt).toLocaleDateString()} by {businessCase.builtBy || 'system'}
            </div>
          )}
        </div>
      </Section>

      {/* ── SECTION 2: MEDDIC QUALIFICATION ──────────────────────────── */}
      <Section title="MEDDIC Qualification" badge={`${meddicComplete}/6 Complete`} defaultOpen={true}>
        <div className="pid-meddic-grid">
          {MEDDIC_FIELDS.map(field => {
            const value  = meddic[field.key] || '';
            const status = meddicStatus(value);
            return (
              <div key={field.key} className={`pid-meddic-field pid-meddic-field--${status}`}>
                <div className="pid-meddic-header">
                  <span className="pid-meddic-letter">{field.letter}</span>
                  <span className="pid-meddic-label">{field.label}</span>
                  <StatusBadge status={status} />
                  <span className="pid-field-ai pid-field-ai--small">{field.ai}</span>
                </div>
                <EditableField
                  value={value}
                  onSave={v => saveMeddic(field.key, v)}
                  placeholder={field.hint}
                />
                {(status === 'missing' || status === 'partial') && MEDDIC_PLAYBOOK[field.key] && (
                  <div className="pid-meddic-nba">
                    <div className="pid-meddic-nba-label">⚡ Next Best Actions</div>
                    {MEDDIC_PLAYBOOK[field.key].map((action, i) => (
                      <div key={i} className="pid-meddic-nba-item">{action}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="pid-meddic-score-bar">
          <div className="pid-meddic-score-label">MEDDIC Completion</div>
          <div className="pid-meddic-bar-track">
            <div
              className="pid-meddic-bar-fill"
              style={{ width: `${(meddicComplete / 6) * 100}%` }}
            />
          </div>
          <div className="pid-meddic-score-pct">{Math.round((meddicComplete / 6) * 100)}%</div>
        </div>
      </Section>

      {/* ── SECTION 3: ENTRY POINTS ───────────────────────────────────── */}
      <Section title="Entry Points" badge={`${entryPoints.length} defined`} defaultOpen={true}>
        {entryPoints.length === 0 && (
          <div className="pid-empty-state">No entry points defined yet. Add your first commercial offer below.</div>
        )}
        {entryPoints.map(ep => (
          <div key={ep.id} className={`pid-ep-card pid-ep-card--${(ep.status||'Proposed').toLowerCase()}`}>
            <div className="pid-ep-header">
              <EditableField
                value={ep.name}
                onSave={v => updateEntryPoint(ep.id, { name: v })}
                placeholder="Entry point name"
                multiline={false}
              />
              <select
                className="pid-ep-status-select"
                value={ep.status || 'Proposed'}
                onChange={e => updateEntryPoint(ep.id, { status: e.target.value })}
              >
                {ENTRY_POINT_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <button className="pid-ep-remove" onClick={() => removeEntryPoint(ep.id)} title="Remove">✕</button>
            </div>
            <div className="pid-ep-body">
              <div className="pid-ep-row">
                <div className="pid-ep-field">
                  <div className="pid-ep-label">Price (Low)</div>
                  <EditableField value={ep.priceLow} onSave={v => updateEntryPoint(ep.id, { priceLow: v })} placeholder="$75K" multiline={false} />
                </div>
                <div className="pid-ep-field">
                  <div className="pid-ep-label">Price (High)</div>
                  <EditableField value={ep.priceHigh} onSave={v => updateEntryPoint(ep.id, { priceHigh: v })} placeholder="$125K" multiline={false} />
                </div>
                <div className="pid-ep-field">
                  <div className="pid-ep-label">Timeline</div>
                  <EditableField value={ep.timeline} onSave={v => updateEntryPoint(ep.id, { timeline: v })} placeholder="4–6 weeks" multiline={false} />
                </div>
              </div>
              <div className="pid-ep-field pid-ep-field--notes">
                <div className="pid-ep-label pid-ep-label--trigger">
                  ⚡ Lead With This When
                </div>
                <EditableField value={ep.trigger} onSave={v => updateEntryPoint(ep.id, { trigger: v })} placeholder='e.g. "Client expresses timeline concern" or "Incumbent risk surfaces in conversation"' multiline={false} />
              </div>
              <div className="pid-ep-field pid-ep-field--notes">
                <div className="pid-ep-label">Notes</div>
                <EditableField value={ep.notes} onSave={v => updateEntryPoint(ep.id, { notes: v })} placeholder="Key deliverables, dependencies, or competitive positioning for this entry point" />
              </div>
            </div>
          </div>
        ))}
        {entryPoints.length > 0 && epHasPrices && (
          <div className="pid-ep-total">
            <span className="pid-ep-total-label">Total Range</span>
            <span className="pid-ep-total-value">
              {epTotalLow > 0 && epTotalHigh > 0
                ? `${fmtPrice(epTotalLow)} – ${fmtPrice(epTotalHigh)}`
                : fmtPrice(epTotalLow || epTotalHigh)}
            </span>
            <span className="pid-ep-total-note">across {entryPoints.length} entry point{entryPoints.length > 1 ? 's' : ''}</span>
          </div>
        )}
        <button className="pid-add-btn" onClick={addEntryPoint}>+ Add Entry Point</button>
      </Section>

      {/* ── SECTION 4: OPEN QUESTIONS ─────────────────────────────────── */}
      <Section title="Open Questions" badge={openQs.filter(q => !q.resolved).length > 0 ? `${openQs.filter(q => !q.resolved).length} open` : 'All resolved'} defaultOpen={true}>
        {openQs.length === 0 && (
          <div className="pid-empty-state">No open questions. Add questions that need to be answered to advance this pursuit.</div>
        )}
        {[...openQs].sort((a, b) => (a.resolved === b.resolved ? 0 : a.resolved ? 1 : -1)).map(q => (
          <div key={q.id} className={`pid-question ${q.resolved ? 'pid-question--resolved' : ''}`}>
            <button
              className="pid-question-check"
              onClick={() => !q.resolved && resolveQuestion(q.id)}
              title={q.resolved ? 'Resolved' : 'Mark as resolved'}
            >
              {q.resolved ? '✓' : '○'}
            </button>
            <div className="pid-question-body">
              <div className="pid-question-text">{q.text}</div>
              <div className="pid-question-meta">
                <span className={`pid-question-ai pid-question-ai--${(q.ai || firstName || 'user').toLowerCase()}`}>{q.ai || firstName || 'user'}</span>
                <span className="pid-question-date">{q.timestamp ? new Date(q.timestamp).toLocaleDateString() : ''}</span>
              </div>
            </div>
          </div>
        ))}
        <div className="pid-add-question-row">
          <input
            className="pid-add-question-input"
            value={newQuestion}
            onChange={e => setNewQuestion(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addQuestion()}
            placeholder="Add a question that needs to be answered…"
          />
          <button className="pid-add-btn pid-add-btn--inline" onClick={addQuestion} disabled={!newQuestion.trim()}>Add</button>
        </div>
      </Section>

      {/* ── SECTION 5: SESSION LOG ────────────────────────────────────── */}
      <Section title="Session Log" badge={`${sessionLog.length} entries`} defaultOpen={false}>
        {sessionLog.length === 0 && (
          <div className="pid-empty-state">No sessions logged yet. The log updates automatically after each Floor or War Room session.</div>
        )}
        <div className="pid-log-list">
          {[...sessionLog].reverse().map((entry, i) => (
            <div key={i} className="pid-log-entry">
              <span className={`pid-log-ai pid-log-ai--${(entry.ai||'system').toLowerCase()}`}>{entry.ai}</span>
              <span className="pid-log-date">{entry.date}</span>
              <span className="pid-log-summary">{entry.summary}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
